'use client';

import type {
  HandleFiltersChanged,
  HandleMapChanged,
  HandleSortChanged,
} from '@/app/components/search/components/SearchPageContainer';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type {
  HandleDynamicFiltersResponseChanged,
  HandleIsLoadingChanged,
} from '@/app/common/PageWrapperClientTypes.tsx';
import { SEARCH_TYPE } from '@/app/search/types.ts';
import React, { startTransition, useEffect, useRef, useState } from 'react';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import SearchPageCommonWrapperDataProvider from '@/app/components/search/searchPageCommonWrapperProvider.tsx';
import type { RouteType } from '@/app/components/search/Curiosity';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { hasAllMandatoryParams } from '@/app/components/search/common/urlValidator.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import type { AuthResponse } from '@/domainLogic/index.ts';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import { createQueryString } from '@/utils/queryUtils.ts';
import type { FiltersGroup, FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import { applyUpdatedFilters } from '@/app/common/filters/core/filterUtils.ts';
import { evoFiltersEnabled } from '@/app/common/filters/core/filterFlags.ts';
import {
  createResetFilterSearchParams,
  createUpdatedFilterSearchParams,
} from '@/app/common/filters/searchParams/createUpdatedSearchParams.ts';
import type { HandleFilterChanged, HandleFiltersReset } from '@/app/common/filters/core/filterComponentType.ts';
import type { DynamicFiltersAPIResponse } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import { dynamicImportCatch } from '@/utils/appUtilsClient.tsx';
import type { MapRef } from 'react-map-gl/mapbox';
import { filtersResetValues } from '@/app/common/filters/core/filterResetValues.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import {
  updateDynamicFilters,
  type DynamicFiltersUpdateResult,
} from '@/app/common/filters/searchParams/updateDynamicFilters.ts';
import { isFilterThatCanFilterItself } from '@/app/common/filters/core/filterDefinitions.ts';
import type { FiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import FiltersUiProvider from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { CUSTOM_LABEL_COMPONENTS } from '@/app/common/filters/surfaces/labels/filtersLabelsUtils.tsx';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import dynamic from 'next/dynamic';
import { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import type { DynamicFiltersRequestPayload } from '@/app/api/search/dynamicfilters/createDynamicFiltersData.ts';
import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { dynamicFiltersResponseDefault } from '@/app/components/search/common/reloadData/dynamicFiltersResponseDefault.ts';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import { createSearchParamsPinkPeopleFromFilters } from '@/app/components/search/pinkpeople/clientPinkPeople.ts';
import {
  applyResultPageFilterEvent,
  getFiltersFromQueryString,
} from '@/app/components/search/components/Filters/utils.ts';
import { createResultPageSortResultPayload } from '@/app/common/starTrack/events/resultPageSortResult.ts';
import { clientAppLogError } from '@/app/components/search/logging/clientAppLog.ts';
import { createResultPageFilterResetPayload } from '@/app/common/starTrack/events/resultPageFilterReset.ts';
import { stripUnavailableFilterParams } from '@/app/common/filters/groups/stripUnavailableParams.ts';
import type { FilterOrder } from '@/app/common/filters/session/filterOrder.ts';
import type { FilterSessionState } from '@/app/common/filters/session/sessionState.ts';
import { sessionInputFrom } from '@/app/common/filters/session/selections.ts';
import { useFilterSession } from '@/app/common/filters/variants/evo/order/useFilterSession.ts';

const IndexClient = dynamic(() => import('@/app/components/search/pages/indexClient.tsx').catch(dynamicImportCatch), {
  ssr: true,
});
const SearchPageContainer = dynamic(
  () => import('@/app/components/search/components/SearchPageContainer').catch(dynamicImportCatch),
  {
    ssr: true,
  }
);

// Preload the module to avoid the flash on the client side
import('@/app/components/search/components/SearchPageContainer');

export type SearchCommonPageData = {
  handleSortChanged: (params: HandleSortChanged) => void;
  handleFiltersChanged: (params: HandleFiltersChanged) => void;
  handleMapChanged: (params: HandleMapChanged) => void;
};

type HandleSortChangedCommonResponse = { searchParamsNormalised: SearchParams };
type HandleSortChangedCommon = ({
  newSortValue,
  searchParamsUpdated,
}: HandleSortChanged) => HandleSortChangedCommonResponse;
type HandleShowFiltersCommonProps = {
  newShowFilters: boolean;
  routeType: RouteType;
};
type HandleShowFiltersCommon = (params: HandleShowFiltersCommonProps) => void;

export type SearchPageCommonWrapperData = {
  setIsLoading: (isLoading: boolean) => void;
  showFilters: boolean;
  filtersGroups: FiltersGroups;
  dynamicFiltersResponse: DynamicFiltersAPIResponse | undefined;
  authResponse?: AuthResponse;
  handleSortChangedCommon: HandleSortChangedCommon;
  handleShowFiltersCommon: HandleShowFiltersCommon;
  handleFilterChanged: HandleFilterChanged;
  handleFiltersReset: HandleFiltersReset;
  filterOrder: FilterOrder;
  filterSession: FilterSessionState;
};

type SearchMainProps = {
  mapRef: React.RefObject<MapRef | null>;
  handleIsLoadingChanged: HandleIsLoadingChanged;
  handleDynamicFiltersResponseChanged: HandleDynamicFiltersResponseChanged;
  handleFiltersGroupsChanged: (newFiltersGroups: FiltersGroups) => void;
};

const SearchMain = ({
  mapRef,
  handleIsLoadingChanged,
  handleDynamicFiltersResponseChanged,
  handleFiltersGroupsChanged,
}: SearchMainProps) => {
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { productsData, filtersGroups, dynamicFilters, filterSession: initialFilterSession } = useSearchPageData();
  const dynamicFiltersResponse = dynamicFilters?.response;
  const { isMobile, isTablet, handleSearchChanged, handleNewSearch, starTrack } = useCommonClient();
  const { curiosityClickEvents } = useCuriosityManager();
  const { translationDictionary } = useTranslation();
  const isClient = useIsClient();

  const { searchType, searchId } = searchInfoData;
  const { authResponse: defaultAuthResponse } = commonData;
  const APIStatus = productsData?.apiStatus || APIStatusEnum.NORESULTS;
  const searchParams = getSearchParams();

  const useEvoFilters = evoFiltersEnabled(localization?.configValuesApp);
  const {
    order: filterOrder,
    session: filterSession,
    recordSelections,
  } = useFilterSession(filtersGroups, initialFilterSession, useEvoFilters);

  const [showFiltersCommon, setShowFiltersCommon] = useState<boolean>(false);
  const [authResponseCommon] = useState<AuthResponse>(defaultAuthResponse);

  // Tracks the productsData instance for which Dynamic Filters has already been requested,
  // preventing duplicate calls when productsData reference changes for unrelated reasons.
  const dynamicFiltersCalledForRef = useRef<ProductsData | null>(null);

  const setIsLoading = (isLoadingNew: boolean) => {
    handleIsLoadingChanged(isLoadingNew);
  };

  const setShowFilters = (showFilters: boolean) => {
    setShowFiltersCommon(showFilters);
  };

  const handleFiltersGroupsUpdated = ({ updatedGroups }: DynamicFiltersUpdateResult) => {
    if (updatedGroups) handleFiltersGroupsChanged(updatedGroups);
  };

  const handleFilterChanged: HandleFilterChanged = (updatedFilters, pressedOrder, tracking) => {
    handleFiltersGroupsChanged(applyUpdatedFilters({ prevFiltersGroups: filtersGroups, updatedFilters }));
    recordSelections(sessionInputFrom(filtersGroups, updatedFilters, pressedOrder));

    const currentSearchParams = getSearchParams();
    const updatedSearchParams = createUpdatedFilterSearchParams({ filtersGroups, updatedFilters });

    const isSortChange = updatedFilters.some((filter) => filter.name === FILTER_NAME.SORT);

    try {
      if (isSortChange) {
        starTrack.resultPageSortResult(
          createResultPageSortResultPayload({ searchParams: updatedSearchParams, localization })
        );
      } else {
        applyResultPageFilterEvent({
          oldFilters: getFiltersFromQueryString(currentSearchParams),
          appliedFilters: getFiltersFromQueryString(updatedSearchParams),
          searchParams: currentSearchParams,
          starTrack,
          localization,
          tracking,
        });
      }
    } catch (error) {
      clientAppLogError({
        level: 'ERROR',
        codeSourceName: 'SearchMain:handleFilterChanged',
        error,
      });
    }

    const searchParamsPinkPeople = createSearchParamsPinkPeopleFromFilters({
      searchParams: updatedSearchParams,
      searchInfoData,
      appliedFilters: updatedSearchParams,
      commonData,
      localization,
    });

    const hasPinkPeopleFilterChanged = !!searchParamsPinkPeople;

    if (hasPinkPeopleFilterChanged) {
      handleNewSearch(searchParamsPinkPeople);
    } else {
      handleSearchChanged({ newSearchParams: updatedSearchParams });
    }

    if (isFilterThatCanFilterItself(updatedFilters)) {
      updateDynamicFilters({
        filtersGroups,
        updatedFilters,
        searchInfoData,
        productsData,
        commonData,
        localization,
        translationDictionary,
        rebuildGroups: true,
        handleFiltersGroupsUpdated,
      });
    }
  };

  const handleFiltersReset: HandleFiltersReset = (position) => {
    const updatedFilters = filtersGroups.flatMap((filtersGroup) =>
      filtersResetValues({ filters: (filtersGroup as FiltersGroup).filters })
    );
    handleFiltersGroupsChanged(applyUpdatedFilters({ prevFiltersGroups: filtersGroups, updatedFilters }));
    recordSelections(sessionInputFrom(filtersGroups, updatedFilters));

    const updatedSearchParams = createResetFilterSearchParams();
    handleSearchChanged({ newSearchParams: updatedSearchParams });

    try {
      starTrack.resultPageFilterReset(
        createResultPageFilterResetPayload({
          searchParams: getSearchParams(),
          position,
        })
      );
    } catch (error) {
      clientAppLogError({
        level: 'ERROR',
        codeSourceName: 'SearchMain:handleFiltersReset',
        error,
      });
    }
  };

  const handleSortChangedCommon = ({
    newSortValue,
    searchParamsUpdated,
  }: HandleSortChanged): { searchParamsNormalised: SearchParams } => {
    setIsLoading(true);
    const searchParamsNormalised = {
      ...removeParameters(searchParamsUpdated, [URLParameters.PAGE, URLParameters.OFFSET, URLParameters.MAP_AREA]),
      [URLParameters.SORT]: newSortValue,
    };
    return { searchParamsNormalised };
  };

  const handleShowFiltersCommon = ({ newShowFilters, routeType }: HandleShowFiltersCommonProps) => {
    const isMobileFilterButton = isMobile || isTablet;
    if (!newShowFilters && isMobileFilterButton) {
      curiosityClickEvents.mobileFiltersOpen(routeType);
    }
    setShowFilters(newShowFilters);
  };

  useEffect(() => {
    if (
      isClient &&
      APIStatus !== APIStatusEnum.WAIT &&
      productsData &&
      productsData !== dynamicFiltersCalledForRef.current
    ) {
      dynamicFiltersCalledForRef.current = productsData;
      const dynamicFiltersPayload: DynamicFiltersRequestPayload = {
        searchParams,
        searchInfoData,
        commonData,
        localization,
      };
      clientAPICall<DynamicFiltersRequestPayload, DynamicFiltersAPIResponse>({
        apiName: API_NAME.DYNAMIC_FILTERS,
        payload: dynamicFiltersPayload,
        commonData,
        defaultResponse: {
          ...dynamicFiltersResponseDefault,
          status: APIStatusEnum.ERROR,
        },
        traceParent: searchInfoData.searchTraceParent,
      }).then((dynamicFiltersResponseInit) => {
        // avoid flicker across consumers on hydration
        startTransition(() => {
          handleDynamicFiltersResponseChanged({
            newDynamicFiltersResponse: dynamicFiltersResponseInit,
            searchParams,
            searchInfoData,
            productsData,
          });
        });
      });
    }
  }, [isClient, productsData]);

  useEffect(() => {
    if (canUseDOM) {
      // TODO: Check other missing params as well?
      const searchParamsFresh = getSearchParams();
      const available = stripUnavailableFilterParams(searchParamsFresh, {
        searchParams: searchParamsFresh,
        searchInfoData,
        localization,
      });
      const shouldAddSearchIdToUrl =
        !searchParamsFresh[URLParameters.SEARCH_ID] && hasAllMandatoryParams(searchParamsFresh);
      const newSearchParams = shouldAddSearchIdToUrl
        ? { ...available, [URLParameters.SEARCH_ID]: searchId }
        : available;
      const newQueryString = createQueryString(newSearchParams);

      if (newQueryString !== createQueryString(searchParamsFresh)) {
        window.history.replaceState(null, '', `?${newQueryString}`);
      }
    }
  }, [canUseDOM]);

  const searchPageCommonWrapperData: SearchPageCommonWrapperData = {
    setIsLoading,
    showFilters: showFiltersCommon,
    filtersGroups,
    dynamicFiltersResponse,
    authResponse: authResponseCommon,
    handleSortChangedCommon,
    handleShowFiltersCommon,
    handleFilterChanged,
    handleFiltersReset,
    filterOrder,
    filterSession,
  };

  // TODO: is this needed? these values are already in context
  const filtersUiData: FiltersUiData = {
    localization,
    isMobile,
    customLabelComponents: CUSTOM_LABEL_COMPONENTS,
    columnOverrides: [{ filterNames: [FILTER_NAME.MEAL_PLAN], columns: 3 }],
    filtersWithRowLayout: [FILTER_NAME.MEAL_PLAN],
    useEvoFilters,
    totalResults: dynamicFiltersResponse?.filters?.total,
  };

  return (
    <SearchPageCommonWrapperDataProvider searchPageCommonWrapperData={searchPageCommonWrapperData}>
      <FiltersUiProvider filtersUiData={filtersUiData}>
        {/* Gate on searchType, not pageType: pageType flips before the new pageWrapperData lands, and the
        results page would mount on the view mode resolved for the page navigated away from. (MPH-931) */}
        {searchType === SEARCH_TYPE.DESTINATIONS ? <IndexClient /> : <SearchPageContainer mapRef={mapRef} />}
      </FiltersUiProvider>
    </SearchPageCommonWrapperDataProvider>
  );
};

export default SearchMain;
